/**
 * @typedef {Object} CartNotificationSection
 * @property {string} id
 * @property {string} [selector]
 */

/**
 * @typedef {Object} CartNotificationParsedState
 * @property {string} key
 * @property {Object.<string, string>} sections
 */

class CartNotification extends HTMLElement {
  constructor() {
    super();

    /** @type {HTMLElement | null} */
    this.notification = document.getElementById('cart-notification');
    /** @type {Element | null} */
    this.header = document.querySelector('sticky-header');
    /** @type {(evt: MouseEvent) => void} */
    this.onBodyClick = this.handleBodyClick.bind(this);
    /** @type {HTMLElement | null} */
    this.activeElement = null;
    /** @type {string} */
    this.cartItemKey = '';

    if (this.notification) {
      this.notification.addEventListener('keyup', /** @param {KeyboardEvent} evt */ (evt) => evt.code === 'Escape' && this.close());
    }
    this.querySelectorAll('button[type="button"]').forEach(/** @param {Element} closeButton */ (closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  /**
   * @returns {void}
   */
  open() {
    if (!this.notification) {return;}
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        if (!this.notification) {return;}
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  /**
   * @returns {void}
   */
  close() {
    if (this.notification) {
      this.notification.classList.remove('active');
    }
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  /**
   * @param {CartNotificationParsedState} parsedState
   * @returns {void}
   */
  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach(/** @param {CartNotificationSection} section */ (section) => {
      const element = document.getElementById(section.id);
      if (element) {
        const content = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
        element.replaceChildren();
        element.insertAdjacentHTML('beforeend', content);
      }
    });

    if (this.header && /** @type {HTMLElement & {reveal?: () => void}} */ (this.header).reveal) {
      /** @type {HTMLElement & {reveal: () => void}} */ (this.header).reveal();
    }
    this.open();
  }

  /**
   * @returns {CartNotificationSection[]}
   */
  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  /**
   * @param {string} html
   * @param {string} [selector]
   * @returns {string}
   */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : '';
  }

  /**
   * @param {MouseEvent} evt
   * @returns {void}
   */
  handleBodyClick(evt) {
    const target = /** @type {Element | null} */ (evt.target);
    if (target && target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? /** @type {HTMLElement | null} */ (disclosure.querySelector('summary')) : null;
      this.close();
    }
  }

  /**
   * @param {HTMLElement | null} element
   * @returns {void}
   */
  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
